/**
 * Acoustic physics calculation module for server-side and client-side tools.
 * Uses speed of sound c = 343 m/s (dry air at 20°C).
 */

export const SPEED_OF_SOUND_MPS = 343;

export interface RoomMode {
  freqHz: number;
  nx: number;
  ny: number;
  nz: number;
  type: 'axial' | 'tangential' | 'oblique';
  label: string;
}

/**
 * Calculates eigenmodes (standing waves) for a rectangular room up to maxFreqHz.
 */
export function calculateRoomModes(
  lengthM: number,
  widthM: number,
  heightM: number,
  maxFreqHz: number = 300
): RoomMode[] {
  const modes: RoomMode[] = [];
  const c = SPEED_OF_SOUND_MPS;

  // Estimate maximum quantum numbers based on max frequency
  const maxNx = Math.ceil((2 * lengthM * maxFreqHz) / c);
  const maxNy = Math.ceil((2 * widthM * maxFreqHz) / c);
  const maxNz = Math.ceil((2 * heightM * maxFreqHz) / c);

  for (let nx = 0; nx <= maxNx; nx++) {
    for (let ny = 0; ny <= maxNy; ny++) {
      for (let nz = 0; nz <= maxNz; nz++) {
        if (nx === 0 && ny === 0 && nz === 0) continue;

        const termX = (nx / lengthM) ** 2;
        const termY = (ny / widthM) ** 2;
        const termZ = (nz / heightM) ** 2;

        const freq = (c / 2) * Math.sqrt(termX + termY + termZ);

        if (freq > maxFreqHz) continue;

        const nonZeroCount = (nx > 0 ? 1 : 0) + (ny > 0 ? 1 : 0) + (nz > 0 ? 1 : 0);
        let type: 'axial' | 'tangential' | 'oblique' = 'axial';
        if (nonZeroCount === 2) type = 'tangential';
        if (nonZeroCount === 3) type = 'oblique';

        modes.push({
          freqHz: Math.round(freq * 10) / 10,
          nx,
          ny,
          nz,
          type,
          label: `(${nx},${ny},${nz})`,
        });
      }
    }
  }

  return modes.sort((a, b) => a.freqHz - b.freqHz);
}

/**
 * Calculates Schroeder crossover frequency: f_s ≈ 2000 * sqrt(RT60 / Volume_m3)
 */
export function calculateSchroederFrequency(volumeM3: number, rt60Seconds: number = 0.4): number {
  if (volumeM3 <= 0) return 200;
  return Math.round(2000 * Math.sqrt(rt60Seconds / volumeM3));
}

export interface SpeakerPlacementResult {
  listenerDistFromFrontWallM: number;
  listenerDistFromFrontWallPercent: number;
  speakerDistFromFrontWallM: number;
  speakerSeparationM: number;
  speakerDistFromSideWallM: number;
  toeInAngleDeg: number;
}

/**
 * Computes recommended speaker and listener placement based on room dimensions.
 */
export function calculateSpeakerPlacement(
  lengthM: number,
  widthM: number
): SpeakerPlacementResult {
  // 38% rule for primary listening position from front wall
  const listenerDistFromFrontWallM = Math.round(lengthM * 0.38 * 100) / 100;
  const listenerDistFromFrontWallPercent = 38;

  // Speakers form an equilateral triangle with listener
  // Typical distance between speakers is ~0.7 to 0.8 of the listener distance to speakers
  const targetTriangleSideM = listenerDistFromFrontWallM * 0.9;
  const speakerSeparationM = Math.round(Math.min(targetTriangleSideM, widthM * 0.6) * 100) / 100;

  const speakerDistFromSideWallM = Math.round(((widthM - speakerSeparationM) / 2) * 100) / 100;

  // Front wall distance to avoid primary SBIR notch
  const speakerDistFromFrontWallM = Math.round(Math.min(lengthM * 0.2, 1.2) * 100) / 100;

  return {
    listenerDistFromFrontWallM,
    listenerDistFromFrontWallPercent,
    speakerDistFromFrontWallM,
    speakerSeparationM,
    speakerDistFromSideWallM,
    toeInAngleDeg: 30,
  };
}

export interface FirstReflectionPoints {
  leftWallDistFromFrontM: number;
  rightWallDistFromFrontM: number;
  ceilingDistFromFrontM: number;
}

/**
 * Calculates first reflection mirror points on side walls and ceiling using geometric optics.
 */
export function calculateFirstReflections(
  lengthM: number,
  widthM: number,
  heightM: number,
  speakerDistFrontM: number,
  speakerSideDistM: number,
  listenerDistFrontM: number
): FirstReflectionPoints {
  const dSpeakerX = speakerSideDistM;
  const dSpeakerY = speakerDistFrontM;
  const dListenerX = widthM / 2;
  const dListenerY = listenerDistFrontM;

  // Mirror point along the length axis (Y) from front wall
  const ratio = dSpeakerX / (dSpeakerX + dListenerX);
  const reflectionY = dSpeakerY + ratio * (dListenerY - dSpeakerY);

  return {
    leftWallDistFromFrontM: Math.round(reflectionY * 100) / 100,
    rightWallDistFromFrontM: Math.round(reflectionY * 100) / 100,
    ceilingDistFromFrontM: Math.round(((dSpeakerY + dListenerY) / 2) * 100) / 100,
  };
}

export interface PanelCoverageResult {
  roomSurfaceAreaM2: number;
  recommendedLightPanelCount: number;
  recommendedStudioPanelCount: number;
  recommendedHeavyPanelCount: number;
  minBassTrapCornersCount: number;
  firstReflectionPanelCount: number;
}

/**
 * Calculates panel count requirements based on surface area and target coverage.
 */
export function calculatePanelCoverage(
  lengthM: number,
  widthM: number,
  heightM: number
): PanelCoverageResult {
  const floorArea = lengthM * widthM;
  const wallArea = 2 * (lengthM * heightM + widthM * heightM);
  const totalSurfaceAreaM2 = Math.round((2 * floorArea + wallArea) * 10) / 10;

  // Typical 2'x4' panel (0.6m x 1.2m) is approx 0.72 m²
  const standardPanelAreaM2 = 0.72;

  // 15% coverage (Light)
  const lightPanelCount = Math.ceil((totalSurfaceAreaM2 * 0.15) / standardPanelAreaM2);
  // 25% coverage (Studio / Critical listening)
  const studioPanelCount = Math.ceil((totalSurfaceAreaM2 * 0.25) / standardPanelAreaM2);
  // 35% coverage (Heavy / Voiceover / Recording)
  const heavyPanelCount = Math.ceil((totalSurfaceAreaM2 * 0.35) / standardPanelAreaM2);

  return {
    roomSurfaceAreaM2: totalSurfaceAreaM2,
    recommendedLightPanelCount: lightPanelCount,
    recommendedStudioPanelCount: studioPanelCount,
    recommendedHeavyPanelCount: heavyPanelCount,
    minBassTrapCornersCount: 4,
    firstReflectionPanelCount: 6, // 2 side walls, 2 front wall, 2 ceiling cloud panels
  };
}
